import React from 'react';
import { Buffer } from 'buffer';
import { createRoot } from 'react-dom/client';

// This needs to be the first stylesheet imported so component stylesheets
// will overwrite it without having to add extra specificity.
import 'scss/app.scss';

import startApp from 'infrastructure/start';
import { errorHandler, ErrorPage } from 'infrastructure/error';

import App from 'containers/App';

import * as serviceWorker from './serviceWorker';

// Ensure Node's Buffer API is available in browsers for Vite-era dependencies.
if (typeof window !== 'undefined' && !window.Buffer) {
  window.Buffer = Buffer;
}

// Lightweight diagnostics capture (errors, console, reporting observer)
(() => {
  const MAX = 100;
  const w = window;
  const browserConsole = w.console;
  if (!w.__diagConsole) w.__diagConsole = [];
  if (!w.__diagErrors) w.__diagErrors = [];
  if (!w.__diagReports) w.__diagReports = [];
  const pushBounded = (arr, item) => {
    try {
      arr.push(item);
      if (arr.length > MAX) arr.splice(0, arr.length - MAX);
    } catch (e) {
      // noop
    }
  };
  // JS errors
  window.addEventListener('error', ev => {
    pushBounded(w.__diagErrors, {
      message: ev?.message || 'error',
      source: ev?.filename,
      lineno: ev?.lineno,
      colno: ev?.colno,
      stack: ev?.error?.stack,
      timestamp: new Date().toISOString(),
    });
  });
  window.addEventListener('unhandledrejection', ev => {
    const reason = ev?.reason;
    pushBounded(w.__diagErrors, {
      message:
        (reason && (reason.message || String(reason))) || 'unhandledrejection',
      stack: reason?.stack,
      timestamp: new Date().toISOString(),
    });
  });
  // Console wrap (captures message text only)
  ['error', 'warn', 'log'].forEach(level => {
    const orig = browserConsole[level];
    browserConsole[level] = function patchedConsole(...args) {
      try {
        const msg = args
          .map(a => {
            try {
              if (typeof a === 'string') return a;
              if (a instanceof Error) return a.message;
              const s = JSON.stringify(a);
              return s?.slice(0, 500);
            } catch (e) {
              return String(a);
            }
          })
          .join(' ');
        pushBounded(w.__diagConsole, {
          level,
          message: msg,
          timestamp: new Date().toISOString(),
        });
      } catch (e) {
        // noop
      }
      return orig.apply(browserConsole, args);
    };
  });
  // Reporting Observer (best effort)
  try {
    // @ts-ignore
    const ReportingObserverCtor = window.ReportingObserver;
    if (ReportingObserverCtor) {
      // @ts-ignore
      const ro = new ReportingObserverCtor(
        reports => {
          reports.forEach(r =>
            pushBounded(w.__diagReports, {
              type: r.type,
              body: r.body,
              timestamp: new Date().toISOString(),
            })
          );
        },
        { buffered: true, types: ['deprecation', 'intervention'] }
      );
      ro.observe();
    }
  } catch (e) {
    // noop
  }
})();

if (process.env.NODE_ENV === 'production') {
  const onerror = errorHandler();
  window.onerror = onerror;
}

const container = document.getElementById('root');
const root = createRoot(container);

startApp(
  () => root.render(<App tab="home" />),
  () => root.render(<ErrorPage canRetry />)
);

serviceWorker.unregister();
